import { createSlice } from '@reduxjs/toolkit';
import { createDistrictUSAMapData,
    createStateUSAMapData,
    findBatchContainingElement,
    getChannelsPartners,
    getOverallPhysicians,
    makeBarDataSeries,
    prepareTopCitySiteChartData,
    sortedDataBasedValue } from '../../utility';
import { getTriggers,
    getTriggersDetails,
    getTriggerEngagementPaginate,
    getAllTriggers,
    getTriggersSummaryPaginate, 
    getTriggersSummaryGraphs, 
    getWebMatrixPatientTrigger, 
    getWebMatrixPatientAnalysisSummaryTrigger } from '../thunks/triggerThunk';


const triggersInitialData = {
    isTriggerLoading: false,
    isLoading: false,
    triggers: [],
    allTriggers: [],
    triggerDetails: {
        name: '',
        physicianSegments: [],
        engagementRate: [],
        messageSequences: [],
        allPhysicians: [],
        filteredPhysicians: [],
        engagement: [],
        creative: [],
        regionalEngagement: [],
        totalTriggers: 0
    },
    currentPage: 1,
    pageSize: 20,
    totalPhysicianCount: 0,
    totalPages: 0,
    isTriggerPaginated: false,
    triggerEngagement: {
        physicians: [],
        search: '',
        currentPage: 1,
        batchNo: 1,
        pageSize: 20,
        batchSize: 40,
        totalPhysicianCount: 0,
        totalRecordsFetch: 0,
        availableBatches: [],
        pageWiseData: {},
        totalPages: 0,
        totalActivePages: 0
    },
    triggerSummary: {
        isTriggerSummaryPaginated: false,
        isTriggerSummaryGraphsLoading: false,
        isFirstTriggerSelection: true,
        isDownloading: false,
        triggerNames: [],
        selectedTriggers: [],
        allPhysicians: [],
        filteredPhysicians: [],
        currentPage: 1,
        pageSize: 25,
        totalPhysicianCount: 0,
        totalPages: 0,
        engagement: [],
        creative: [],
        regionalEngagement: [],
        totalTriggers: 0,
        overallPhysicians: 0,
        stateOverallPhysicians: 0,
        distributionRegion: [],
        stateDistributionRegion: [],
        districtBreakdownUSAMapData: [],
        stateBreakdownUSAMapData: [],
        stateMapSeries: []
    },
    triggerMapDetails: {
        overallPhysicians: 0,
        stateOverallPhysicians: 0,
        distributionRegion: [],
        stateDistributionRegion: [],
        districtBreakdownUSAMapData: [],
        stateBreakdownUSAMapData: [],
        stateMapSeries: []
    },
    dtcData: {
        isWebMatrixPatientLoading: false,
        isWebMatrixPatientSummaryLoading: false,
        dtcTableData: [],
        filteredDtcTableData: [],
        currentPage: 1,
        pageSize: 20,
        totalRecordsFetch: 0,
        totalPages: 0,
        topCitiesChartData: {
            series: [],
            categories: []
        },
        topSitesChartData: {
            series: [],
            categories: []
        }
    }
};
const triggerSlice = createSlice({
    name: 'trigger',
    initialState: triggersInitialData,
    reducers: {
        resetToInitialState(state, action) {
            Object.assign(state, triggersInitialData);
        },
        resetDtcTableStates(state, action) {
            return {
                ...state,
                dtcData: {
                    ...state.dtcData,
                    isWebMatrixPatientSummaryLoading: false,
                    dtcTableData: [],
                    filteredDtcTableData: [],
                    pageSize: 20,
                    totalRecordsFetch: 0,
                    totalPages: 0
                }
            };
        },     
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setTriggerSummaryCurrentPage: (state, action) => {
            state.triggerSummary.currentPage = action.payload;
        },
        setDtcTableCurrentPage: (state, action) => {
            state.dtcData.currentPage = action.payload;
        },
        setDownloading: (state, action) => {
            state.triggerSummary.isDownloading = action.payload;
        },
        setSelectedTriggers: (state, action) => {
            const { option } = action.payload;
            const isFirstTime = state.triggerSummary.isFirstTriggerSelection;
            
            state.triggerSummary = {
                ...state.triggerSummary,
                allPhysicians: [],
                filteredPhysicians: [],
                currentPage: 1,
                pageSize: 20,
                totalPhysicianCount: 0,
                totalPages: 0,
                engagement: [],
                creative: [{
                    name: '',
                    data: []
                }],
                regionalEngagement: [],
                totalTriggers: 0,
                isTriggerSummaryPaginated: false,
                isTriggerSummaryGraphsLoading: false,
                isDownloading: false,
                overallPhysicians: 0,
                stateOverallPhysicians: 0,
                distributionRegion: [],
                stateDistributionRegion: [],
                districtBreakdownUSAMapData: [],
                stateBreakdownUSAMapData: [],
                stateMapSeries: []
            };
        
            if (isFirstTime && option.value !=='All') {
                state.triggerSummary.isFirstTriggerSelection = false;
                state.triggerSummary.selectedTriggers = [option.value];
            } else{
                if(option.value === 'All' && !state.triggerSummary.isFirstTriggerSelection){
                    state.triggerSummary.isFirstTriggerSelection = true;
                    state.triggerSummary.selectedTriggers = state.triggerSummary.triggerNames;
                } else{
                    state.triggerSummary.selectedTriggers = [...state.triggerSummary.selectedTriggers, option.value].filter(
                        (item, index, arr) => arr.indexOf(item) === index
                    );
                }
            }
            
            
        },
        
        handleRemoveSelectedTrigger: (state, action) => {
            const { trigger } = action.payload;
            const updatedTriggers = state.triggerSummary.selectedTriggers
                .filter((item) => item !== trigger);
            if(updatedTriggers.length === 0 && !state.triggerSummary.isFirstTriggerSelection){
                state.triggerSummary.isFirstTriggerSelection = true;
                state.triggerSummary.selectedTriggers = state.triggerSummary.triggerNames;
            } else{
                state.triggerSummary.selectedTriggers = updatedTriggers;
            }
        },
        
        handleSearch: (state, action) => {
            const { searchTerm } = action.payload;

            // Transforming providers
            const updatedPhysician = state.triggerDetails.allPhysicians.map(
                (physician) => ({
                    ...physician,
                    addresses:
                        physician?.address +
                        ', ' +
                        physician?.city +
                        ', ' +
                        physician?.state +
                        ',' +
                        physician?.zip,
                    segmentations: Array.isArray(physician.segment)
                        ? physician.segment.join(', ')
                        : physician.segment
                })
            );

            // Filtering providers based on searchTerm
            const filteredPhysicians = updatedPhysician.filter((physician) => {
                const physicianData = Object.values(physician)
                    .map((value) =>
                        typeof value === 'string'
                            ? value.trim().toLowerCase()
                            : value
                    )
                    .join(' ');
                const trimmedSearchTerm = searchTerm?.trim()?.toLowerCase();
                return physicianData.includes(trimmedSearchTerm);
            });

            return {
                ...state,
                triggerDetails: {
                    ...state.triggerDetails,
                    filteredPhysicians: filteredPhysicians
                },
                totalPhysicianCount: filteredPhysicians.length,
                totalPages: Math.ceil(
                    filteredPhysicians.length / (state.pageSize ?? 1)
                ),
                currentPage: 1
            };
        },
        handleTriggerSummarySearch: (state, action) => {
            const { searchTerm } = action.payload;
            if (!searchTerm?.trim()) {
                return {
                    ...state,
                    triggerSummary: {
                        ...state.triggerSummary,
                        filteredPhysicians: state.triggerSummary.allPhysicians
                    },
                    totalPages: Math.ceil(state.triggerSummary.totalPhysicianCount / (state.pageSize ?? 1))
                };
            }

            // Transforming providers
            const updatedPhysician = state.triggerSummary.allPhysicians.map(
                (physician) => ({
                    ...physician,
                    addresses:
                        physician?.address +
                        ', ' +
                        physician?.city +
                        ', ' +
                        physician?.state +
                        ',' +
                        physician?.zip,
                    segmentations: Array.isArray(physician.segment)
                        ? physician.segment.join(', ')
                        : physician.segment
                })
            );

            // Filtering providers based on searchTerm
            const filteredPhysicians = updatedPhysician.filter((physician) => {
                const physicianData = Object.values(physician)
                    .map((value) =>
                        typeof value === 'string'
                            ? value.trim().toLowerCase()
                            : value
                    )
                    .join(' ');
                const trimmedSearchTerm = searchTerm?.trim()?.toLowerCase();
                return physicianData.includes(trimmedSearchTerm);
            });

            return {
                ...state,
                triggerSummary: {
                    ...state.triggerSummary,
                    filteredPhysicians: filteredPhysicians
                },
                totalPages: Math.ceil(
                    filteredPhysicians.length / (state.pageSize ?? 1)
                )
            };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTriggers.fulfilled, (state, action) => {
                state.isTriggerLoading = false;
                state.error = null;
                let dataSources = action.payload.triggers || [];
                dataSources = [].concat(...dataSources);
                state.triggers = dataSources;
                const triggerNames = dataSources.map(
                    (item) => item.name
                );
                state.triggerSummary.triggerNames = triggerNames;
                state.triggerSummary.selectedTriggers = triggerNames;
            })
            .addCase(getTriggers.pending, (state, action) => {
                state.isTriggerLoading = true;
                state.error = null;
            })
            .addCase(getTriggers.rejected, (state, action) => {
                state.isTriggerLoading = false;
                state.error = action.payload;
            })
            // .addCase(getTriggersDetails.fulfilled, (state, action) => {
            //     state.isTriggerLoading = false;
            //     if (
            //         action.payload.triggerEngagement &&
            //         Object.keys(action.payload.triggerEngagement).length > 3
            //     ) {
            //         state.triggerDetails = {
            //             ...state.triggerDetails,
            //             ...action.payload?.triggerEngagement
            //         };
            //         state.triggerDetails.allPhysicians = sortedDataBasedValue(
            //             action.payload.triggerEngagement?.physicians,
            //             'Match'
            //         );
            //         state.triggerDetails.filteredPhysicians =
            //             sortedDataBasedValue(
            //                 action.payload.triggerEngagement?.physicians,
            //                 'Match'
            //             );
            //         /* Total Physician Count */
            //         state.totalPhysicianCount =
            //             action.payload.triggerEngagement?.physicians?.length ??
            //             0;
            //         /* Total Pages */
            //         state.totalPages = Math.ceil(
            //             (action.payload.triggerEngagement?.physicians?.length ??
            //                 0) / state.pageSize
            //         );
            //         /* Engagement Rate */
            //         if (action.payload.triggerEngagement.engagementRate) {
            //             state.triggerDetails.engagement = Object.entries(
            //                 action.payload.triggerEngagement.engagementRate
            //             ).map((item) => {
            //                 const [key, value] = item;
            //                 const engagementLabel = getChannelsPartners(
            //                     key,
            //                     'engagement'
            //                 );

        //                 return {
        //                     name: engagementLabel,
        //                     y: value
        //                 };
        //             });
        //         }
        //         if (action.payload.triggerEngagement.sorted_date_counts) {
        //             let total = 0;
        //             action.payload.triggerEngagement.sorted_date_counts?.forEach(
        //                 (entry) => {
        //                     total += entry[1];
        //                 }
        //             );
        //             state.triggerDetails.totalTriggers = total || 0;
        //             const creativeData =
        //                 action.payload.triggerEngagement.sorted_date_counts.map(
        //                     ([date, value]) => ({
        //                         x: date,
        //                         y: value
        //                     })
        //                 );
        //             state.triggerDetails.creative = [
        //                 {
        //                     name: 'Trigger Count',
        //                     data: creativeData
        //                 }
        //             ];
        //         }
        //     } else {
        //         Object.assign(state, triggersInitialData);
        //     }
        //     state.error = null;
        // })
        // .addCase(getTriggersDetails.pending, (state, action) => {
        //     state.isTriggerLoading = true;
        //     state.error = null;
        // })
        // .addCase(getTriggersDetails.rejected, (state, action) => {
        //     state.isTriggerLoading = false;
        //     state.error = action.payload;
        //     Object.assign(state, triggersInitialData);
        // })
        // .addCase(
        //     getTriggerEngagementPaginate.fulfilled,
        //     (state, action) => {
        //         state.isTriggerPaginated = false;
        //         if (
        //             action.payload.triggerEngagement.physicians &&
        //             action.payload.triggerEngagement.totalPhysicianCount
        //         ) {
        //             /* Total Physician Count */
        //             state.triggerEngagement.totalPhysicianCount =
        //                 action.payload?.triggerEngagement?.totalPhysicianCount;
        //             /* Total Pages */
        //             state.triggerEngagement.totalPages = Math.ceil(
        //                 action.payload.triggerEngagement
        //                     .totalPhysicianCount /
        //                     state.triggerEngagement.pageSize
        //             );
        //             /* Total Records Fetch */
        //             state.triggerEngagement.totalRecordsFetch +=
        //                 action.payload.triggerEngagement?.physicians
        //                     ?.length || 0;
        //             /* Total Active Pages */
        //             state.triggerEngagement.totalActivePages = Math.ceil(
        //                 state.triggerEngagement.totalRecordsFetch /
        //                     state.triggerEngagement.pageSize
        //             );

        //             const { batchNumber, firstElementOfBatch } =
        //                 findBatchContainingElement(
        //                     state.triggerEngagement.currentPage,
        //                     state.triggerEngagement.totalPhysicianCount,
        //                     state.triggerEngagement.batchSize /
        //                         state.triggerEngagement.pageSize
        //                 );
        //             /* Batch Number */
        //             state.triggerEngagement.batchNo = batchNumber;
        //             /* Records Fetch in that Batch*/
        //             const recordsFetch =
        //                 action.payload?.triggerEngagement?.physicians
        //                     ?.length;
        //             /* Page Count */
        //             let pageCount = state.triggerEngagement.pageSize;
        //             /* Total Pages According to Record Fetch in that Batch */
        //             const totalPages = Math.ceil(
        //                 recordsFetch / state.triggerEngagement.pageSize
        //             );
        //             /* Separate Records according to Batch */
        //             const separatedPageWiseData = {};
        //             for (let i = 0; i < totalPages; i++) {
        //                 if (i == 0) {
        //                     separatedPageWiseData[firstElementOfBatch] =
        //                         action.payload.triggerEngagement.physicians.slice(
        //                             i,
        //                             pageCount - 1
        //                         );
        //                     pageCount =
        //                         pageCount +
        //                         state.triggerEngagement.pageSize -
        //                         1;
        //                 } else {
        //                     separatedPageWiseData[firstElementOfBatch + i] =
        //                         action.payload.triggerEngagement.physicians.slice(
        //                             i * state.triggerEngagement.pageSize,
        //                             pageCount
        //                         );
        //                     pageCount =
        //                         pageCount +
        //                         state.triggerEngagement.pageSize;
        //                 }
        //             }
        //             state.triggerEngagement.pageWiseData = {
        //                 ...state.triggerEngagement.pageWiseData,
        //                 ...separatedPageWiseData
        //             };
        //             /* Push the new records to Physicians Array */
        //             state.triggerEngagement.physicians.push(
        //                 ...action.payload?.triggerEngagement?.physicians
        //             );
        //             /* Available Batches */
        //             state.triggerEngagement.availableBatches.push(
        //                 state.triggerEngagement.batchNo
        //             );
                        
            //         }
            //         state.error = null;
            //     }
            // )
            // .addCase(getTriggerEngagementPaginate.pending, (state, action) => {
            //     state.isTriggerPaginated = true;
            //     state.error = null;
            // })
            // .addCase(getTriggerEngagementPaginate.rejected, (state, action) => {
            //     state.isTriggerPaginated = false;
            //     state.error = action.payload;
            // })
            .addCase(getAllTriggers.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.status_code) {
                    state.allTriggers = action.payload?.triggers || [];
                } else {
                    console.error(
                        'Error in get All Triggers',
                        action.payload?.error
                    );
                }
            })
            .addCase(getAllTriggers.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllTriggers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(getTriggersSummaryPaginate.fulfilled, (state, action) => {
                state.triggerSummary.isTriggerSummaryPaginated = false;
               
                const { meta } = action;
                const { type } = meta.arg;
                if(type !== 'download'){
                    const totalData = action.payload.triggerEngagement?.total_trigger_counts || 0;
                    if (totalData > 0) {
                        state.triggerSummary.allPhysicians = sortedDataBasedValue(action.payload.triggerEngagement?.physicians,'Match');
                        state.triggerSummary.filteredPhysicians = sortedDataBasedValue(action.payload.triggerEngagement?.physicians,'Match');
                        /* Total Physician Count */
                        state.triggerSummary.totalPhysicianCount = totalData || 0;
                        /* Total Pages */
                        state.triggerSummary.totalPages = Math.ceil((totalData) / state.triggerSummary.pageSize);
                        /* Engagement Rate */
                        // if (action.payload.triggerEngagement.engagementRate) {
                        //     state.triggerDetails.engagement = Object.entries(
                        //         action.payload.triggerEngagement.engagementRate
                        //     ).map((item) => {
                        //         const [key, value] = item;
                        //         const engagementLabel = getChannelsPartners(
                        //             key,
                        //             'engagement'
                        //         );

                        //         return {
                        //             name: engagementLabel,
                        //             y: value
                        //         };
                        //     });
                        // }
                        // if (action.payload.triggerEngagement.sorted_date_counts) {
                        //     let total = 0;
                        //     const sortedData = Object.entries(action.payload.triggerEngagement.sorted_date_counts || {});
                        //     sortedData.forEach(([_, value]) => {
                        //         total += value.total_counts;
                        //     });
                        
                        //     state.triggerDetails.totalTriggers = total || 0;
                        
                        //     const creativeData = sortedData.map(([date, value]) => ({
                        //         x: value.date,
                        //         y: value.total_counts
                        //     }));
                        
                        //     state.triggerDetails.creative = [
                        //         {
                        //             name: 'Trigger Count',
                        //             data: creativeData
                        //         }
                        //     ];
                        // }
                        
                    } else {
                        state.triggerSummary.allPhysicians = [];
                        state.triggerSummary.filteredPhysicians = [];
                        /* Total Physician Count */
                        state.triggerSummary.totalPhysicianCount = totalData;
                        /* Total Pages */
                        state.triggerSummary.totalPages = 0;
                        state.triggerSummary.totalTriggers = 0;
                    }
                }
               
                state.error = null;
            })
            .addCase(getTriggersSummaryPaginate.pending, (state, action) => {
                const { meta } = action;
                const { type } = meta.arg;
                if(type !== 'download'){
                    state.triggerSummary.isTriggerSummaryPaginated = true;
                    state.error = null;
                }
                
            })
            .addCase(getTriggersSummaryPaginate.rejected, (state, action) => {
                state.triggerSummary.isTriggerSummaryPaginated = false;
                state.error = action.payload;
            })
            .addCase(getTriggersSummaryGraphs.fulfilled, (state, action) => {
                state.triggerSummary.isTriggerSummaryGraphsLoading = false;
                /* Engagement Rate for HCP dashboards */
                if (action.payload.triggerEngagement.engagementRate) {
                    state.triggerSummary.engagement = Object.entries(
                        action.payload.triggerEngagement.engagementRate
                    ).map((item) => {
                        const [key, value] = item;
                        const engagementLabel = getChannelsPartners(
                            key,
                            'engagement'
                        );

                        return {
                            name: engagementLabel,
                            y: value
                        };
                    });
                }
                // area chart logics for DTC Dashboards
                if (action.payload.triggerEngagement.sorted_date_counts) {
                    // to show total triggers in filter bar for both DTC and HCP dashboards
                    state.triggerSummary.totalTriggers = action.payload.triggerEngagement?.total_trigger_counts || 0;
                    const creativeData =
                    action.payload.triggerEngagement.sorted_date_counts.map(
                        (item) => ({
                            x: item.date,
                            y: item.total_counts,
                            counts: item.counts,
                            date: item.date
                        })
                    );
                    state.triggerSummary.creative = [
                        {
                            name: 'Trigger Count',
                            data: creativeData
                        }
                    ];
                }
                // district/state breakdown for trigger summary
                state.triggerSummary.overallPhysicians = getOverallPhysicians(action.payload?.regionalEngagement || []);
                state.triggerSummary.stateOverallPhysicians = getOverallPhysicians(action.payload?.stateEngagement || []);
                state.triggerSummary.distributionRegion = makeBarDataSeries(action.payload?.regionalEngagement || []) || [];
                state.triggerSummary.stateDistributionRegion = makeBarDataSeries(action.payload?.stateEngagement,false,true) || [];
                state.triggerSummary.districtBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.regionalEngagement || [] ) ;
                state.triggerSummary.stateBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.stateEngagement || [] );
                state.triggerSummary.stateMapSeries = createStateUSAMapData(action.payload?.stateEngagement || []);

                // district/state breakdown for trigger
                state.triggerMapDetails.overallPhysicians = getOverallPhysicians(action.payload?.regionalEngagement || []);
                state.triggerMapDetails.stateOverallPhysicians = getOverallPhysicians(action.payload?.stateEngagement || []);
                state.triggerMapDetails.distributionRegion = makeBarDataSeries(action.payload?.regionalEngagement || []) || [];
                state.triggerMapDetails.stateDistributionRegion = makeBarDataSeries(action.payload?.stateEngagement,false,true) || [];
                state.triggerMapDetails.districtBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.regionalEngagement || [] ) ;
                state.triggerMapDetails.stateBreakdownUSAMapData = createDistrictUSAMapData(action.payload?.stateEngagement || [] );
                state.triggerMapDetails.stateMapSeries = createStateUSAMapData(action.payload?.stateEngagement || []);
                state.error = null;
            })
            .addCase(getTriggersSummaryGraphs.pending, (state, action) => {
                state.triggerSummary.isTriggerSummaryGraphsLoading = true;
                state.error = null;
            })
            .addCase(getTriggersSummaryGraphs.rejected, (state, action) => {
                state.triggerSummary.isTriggerSummaryGraphsLoading = false;
                state.error = action.payload;
            })
            .addCase(getWebMatrixPatientTrigger.fulfilled, (state, action) => {
                const webPatients = action.payload || {};
                if(webPatients?.data && Object.keys(webPatients?.data).length){
                //table data
                    // state.dtcData.dtcTableData = webPatients?.data?.audience_action_summary || [];
                    // state.dtcData.filteredDtcTableData = webPatients?.data?.audience_action_summary || [];
                    // state.dtcData.totalRecordsFetch = webPatients?.data?.audience_action_summary?.length;
                    // state.dtcData.totalPages = Math.ceil((webPatients?.data?.audience_action_summary?.length ?? 0) / state.dtcData.pageSize);
                    //top cities and sites data
                    const topCharts = webPatients?.data?.top_charts || {};
                    state.dtcData.topCitiesChartData.series = prepareTopCitySiteChartData(topCharts,'cities').series;
                    state.dtcData.topCitiesChartData.categories = prepareTopCitySiteChartData(topCharts,'cities').categories;
                    state.dtcData.topSitesChartData.series = prepareTopCitySiteChartData(topCharts,'sites').series;
                    state.dtcData.topSitesChartData.categories = prepareTopCitySiteChartData(topCharts,'sites').categories;
                }
                state.dtcData.isWebMatrixPatientLoading = false;
            })
            .addCase(getWebMatrixPatientTrigger.pending, (state, action) => {
                state.dtcData.isWebMatrixPatientLoading = true;
                state.error = null;
            })
            .addCase(getWebMatrixPatientTrigger.rejected, (state, action) => {
                state.dtcData.isWebMatrixPatientLoading = false;
                state.error = action.payload;
            })
            .addCase(getWebMatrixPatientAnalysisSummaryTrigger.fulfilled, (state, action) => {
                const webPatientsTableData = action.payload || {};
                state.dtcData.dtcTableData = webPatientsTableData?.data || [];
                state.dtcData.filteredDtcTableData = webPatientsTableData?.data || [];
                state.dtcData.totalRecordsFetch = webPatientsTableData?.total_count || 0;
                state.dtcData.totalPages = Math.ceil((webPatientsTableData?.total_count ?? 0) / state.dtcData.pageSize);
                state.error = null; 
                state.dtcData.isWebMatrixPatientSummaryLoading = false;
            })
            .addCase(getWebMatrixPatientAnalysisSummaryTrigger.pending, (state, action) => {
                state.dtcData.isWebMatrixPatientSummaryLoading = true;
                state.error = null;
            })
            .addCase(getWebMatrixPatientAnalysisSummaryTrigger.rejected, (state, action) => {
                state.dtcData.isWebMatrixPatientSummaryLoading = false;
                state.error = action.payload;
            });

    }
});

export const { actions } = triggerSlice;
export default triggerSlice.reducer;
